import { countryList } from '../constants/countries';
import {
  COUNTRY_CODE_PATTERN,
  COUNTRY_STORAGE_KEYS,
  COUNTRY_STORAGE_PREFIX,
  DETECTED_COUNTRY_HEADER,
  UNKNOWN_COUNTRY,
  US_COUNTRY,
} from '../constants/country';
import { ISelectorValue } from '../types/shell';
import { IInitKeys } from '../types/shipping';

export const normalizeCountryCode = (value?: string | null): string | null => {
  const code = value?.trim().toUpperCase() ?? '';

  return COUNTRY_CODE_PATTERN.test(code) ? code : null;
};

export const isUsCountry = (code?: string | null): boolean => normalizeCountryCode(code) === US_COUNTRY;

export const isUnknownCountry = (code?: string | null): boolean => {
  const normalized = normalizeCountryCode(code);

  return !normalized || normalized === UNKNOWN_COUNTRY;
};

export const readDetectedCountry = (headers?: Headers | null): string | null =>
  normalizeCountryCode(headers?.get(DETECTED_COUNTRY_HEADER));

let regionNames: Intl.DisplayNames | null | undefined;

const getRegionNames = (): Intl.DisplayNames | null => {
  if (regionNames === undefined) {
    try {
      regionNames =
        typeof Intl !== 'undefined' && 'DisplayNames' in Intl
          ? new Intl.DisplayNames(['en'], { type: 'region' })
          : null;
    } catch {
      regionNames = null;
    }
  }

  return regionNames;
};

export const getCountryDisplayName = (code?: string | null): string | undefined => {
  const iso = normalizeCountryCode(code);

  if (!iso || iso === UNKNOWN_COUNTRY) {
    return undefined;
  }

  let name: string | undefined;

  try {
    name = getRegionNames()?.of(iso) ?? undefined;
  } catch {
    name = undefined;
  }

  if (name && name !== iso) {
    return name;
  }

  return countryList.find((country) => country.two_digit_code === iso)?.name;
};

export const getClaimStorageId = (keys: IInitKeys): string | null => keys.one_link_id || keys.order_id || null;

const buildStorageKey = (claimId: string, key: string): string => `${COUNTRY_STORAGE_PREFIX}:${claimId}:${key}`;

export const readClaimSessionValue = (keys: IInitKeys, key: string): string | null => {
  const claimId = getClaimStorageId(keys);

  if (!claimId) {
    return null;
  }

  try {
    return window.sessionStorage.getItem(buildStorageKey(claimId, key));
  } catch {
    return null;
  }
};

export const writeClaimSessionValue = (keys: IInitKeys, key: string, value: string | null): void => {
  const claimId = getClaimStorageId(keys);

  if (!claimId) {
    return;
  }

  try {
    const storageKey = buildStorageKey(claimId, key);

    if (value === null) {
      window.sessionStorage.removeItem(storageKey);
    } else {
      window.sessionStorage.setItem(storageKey, value);
    }
  } catch {
    return;
  }
};

export const readStoredUserCountry = (keys: IInitKeys): string | null =>
  normalizeCountryCode(readClaimSessionValue(keys, COUNTRY_STORAGE_KEYS.userCountry));

export const storeUserCountry = (keys: IInitKeys, iso: string | null): void =>
  writeClaimSessionValue(keys, COUNTRY_STORAGE_KEYS.userCountry, normalizeCountryCode(iso));

let countryOptions: ISelectorValue[] | undefined;

export const getCountryOptions = (): ISelectorValue[] => {
  if (!countryOptions) {
    countryOptions = countryList
      .map((country) => ({
        value: country.two_digit_code,
        label: getCountryDisplayName(country.two_digit_code) ?? country.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'en'));
  }

  return countryOptions;
};

export const readHintDismissed = (keys: IInitKeys): boolean =>
  readClaimSessionValue(keys, COUNTRY_STORAGE_KEYS.hintDismissed) === '1';

export const storeHintDismissed = (keys: IInitKeys): void =>
  writeClaimSessionValue(keys, COUNTRY_STORAGE_KEYS.hintDismissed, '1');
